import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Downloads",
  "includeToc": true,
  "layout": "downloads",
  "description": "You can download data for production volumes, revenue, and disbursements here. All data is from the Office of Natural Resources Revenue.",
  "tags": ["Data", "Downloads", "Documentation", "Validation", "Collection"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "Downloads",
      "style": {
        "position": "relative"
      }
    }}>{`Downloads`}</h1>
    <p>{`You can download data for, production volumes, revenue, federal sales and disbursements here.
All data is from the `}<a parentName="p" {...{
        "href": "https://www.onrr.gov/"
      }}>{`Office of Natural Resources Revenue`}</a>{`.`}</p>
    <h2 {...{
      "id": "Production",
      "style": {
        "position": "relative"
      }
    }}>{`Production`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Production-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Production by month`}</h3>
    <p>{`This dataset includes commodity volumes of natural resources by month. Natural resources are extracted from federal lands, federal waters, and Native American lands. We offer data from January 2003 through the most recently available month.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/production-by-month/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Production-by-year",
      "style": {
        "position": "relative"
      }
    }}>{`Production by year`}</h3>
    <p>{`This dataset includes commodity volumes of natural resources by year. Natural resources are extracted from federal lands, federal waters, and Native American lands. Federal production volumes are available by state, county, and offshore area. Location data is `}<GlossaryTerm mdxType="GlossaryTerm">{`withheld`}</GlossaryTerm>{` for Native American data. We offer production data for calendar and fiscal years.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/production/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Production-disposition-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Production disposition by month`}</h3>
    <p>{`This dataset contains monthly production disposition volumes from ONRR’s OGOR-B form. These volumes come from the production of oil and gas. Production locations include federal lands, federal waters, and Native American lands. `}</p>
    <p>{`Disposition descriptions of the product include: `}</p>
    <ul>
      <li parentName="ul">{`directly removed/sold`}</li>
      <li parentName="ul">{`directly used (injected)`}</li>
      <li parentName="ul">{`moved off the lease `}</li>
      <li parentName="ul">{`transferred to a different facility`}</li>
      <li parentName="ul">{`flared`}</li>
      <li parentName="ul">{`spilled and/or lost`}</li>
      <li parentName="ul">{`lost through evaporation/shrinkage`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/production-by-disposition/"
      }}>{`Downloads and documentation`}</a></p>
    <h2 {...{
      "id": "Revenue",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Revenue-1",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h3>
    <p>{`There are three revenue datasets offered: `}</p>
    <ul>
      <li parentName="ul">{`Revenue by month for January 2003 through the most recently completed month of the current year. `}</li>
      <li parentName="ul">{`Revenue by calendar year from 2003 through the most recently completed year.`}</li>
      <li parentName="ul">{`Revenue by fiscal year from 2004 through the most recently completed year. `}</li>
    </ul>
    <p>{`The data includes revenue from federal lands, federal waters, and Native American lands. The data is further broken down by location, lease type, revenue type, and commodity information.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/revenue/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Historical-revenue",
      "style": {
        "position": "relative"
      }
    }}>{`Historical revenue`}</h3>
    <p>{`This dataset offers revenue by calendar year from 1982 through 2002. The data includes revenue from federal lands and federal waters. The data is further broken down by location, revenue type, and commodity information.`}</p>
    <br />
    <p>{`Download historical revenue data:`}</p>
    <Link linkType="DownloadCsv" href="/downloads/historical_revenuecsv_CY1982-2002.csv" mdxType="Link">
  Historical revenue, CY 1982 - 2002
    </Link>
    <Link linkType="DownloadXls" href="/downloads/historical_revenue_CY1982-2002.xlsx" mdxType="Link">
  Historical revenue, CY 1982 - 2002
    </Link>
    <h3 {...{
      "id": "Federal-revenue-by-company",
      "style": {
        "position": "relative"
      }
    }}>{`Federal revenue by company`}</h3>
    <p>{`This dataset provides natural resource federal revenue by company. It includes revenue types by commodity for companies extracting natural resources on federal lands and waters. It does not include company revenue from Native American lands or privately owned lands.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/federal-revenue-by-company/"
      }}>{`Downloads and documentation`}</a></p>
    <h2 {...{
      "id": "Federal-sales",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Federal-sales-by-calendar-year",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales by calendar year`}</h3>
    <p>{`This dataset contains federal sales data for oil, gas, and natural gas liquids (NGLs) that report volumes for royalty and non-royalty bearing transactions by calendar year. This federal sales data represents sales transactions that took place within a given calendar year. The dataset is for calendar years 2013-2023.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/federal-sales/"
      }}>{`Downloads and documentation`}</a></p>
    <h2 {...{
      "id": "Disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Disbursements-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements by month`}</h3>
    <p>{`The amount of money earned from the extraction of natural resources on federal lands is disbursed to various legislated funds every month.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/disbursements-by-month/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Disbursements-by-year",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements by year`}</h3>
    <p>{`The amount of money earned from the extraction of natural resources on federal lands is disbursed to various legislated funds by fiscal year.
`}<a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/disbursements/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Historical-disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Historical disbursements`}</h3>
    <p>{`This dataset offers disbursements data by fiscal year from 1982 through 2002.`}</p>
    <br />
    <p>{`Download historical disbursements data:`}</p>
    <Link linkType="DownloadCsv" href="/downloads/historical_disbursementscsv_ FY1982-2002.csv" mdxType="Link">
  Historical disbursements, FY 1982 - 2002
    </Link>
    <Link linkType="DownloadXls" href="/downloads/historical_disbursements_FY1982-2002.xlsx" mdxType="Link">
  Historical disbursements, FY 1982 - 2002
    </Link>
    <h2 {...{
      "id": "Data-collection-and-validation",
      "style": {
        "position": "relative"
      }
    }}>{`Data collection and validation`}</h2>
    <hr></hr>
    <p>{`As a steward of natural resources and energy revenue, the Office of Natural Resources Revenue (ONRR) handles three main functions. These functions are: collecting the revenue, verifying the revenue, and disbursing the revenue. ONRR collects and manages data to do its tasks. This data is the foundation for this site’s production, revenue, and disbursement datasets.`}</p>
    <h3 {...{
      "id": "Process",
      "style": {
        "position": "relative"
      }
    }}>{`Process`}</h3>
    <p>{`ONRR's data management processes include collection and validation.`}</p>
    <h4 {...{
      "id": "Collection",
      "style": {
        "position": "relative"
      }
    }}>{`Collection`}</h4>
    <p>{`Each month, companies report data to ONRR on the following:`}</p>
    <ul>
      <li parentName="ul">{`The amount of resources they produced and sold. `}</li>
      <li parentName="ul">{`The value of their sold or used production. `}</li>
      <li parentName="ul">{`The amount of royalties they owe the federal government. `}</li>
    </ul>
    <p>{`Oil and gas companies are required to submit two key monthly reports to ONRR. The first report is the `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/production?tabs=forms"
      }}>{`Oil and Gas Operations Report (OGOR) `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. The OGOR report specifies the total production and disposition of oil and gas. The second report is the `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/revenue?tabs=forms"
      }}>{`Sales and Royalty Remittance Report, Form ONRR-2014 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. The ONRR-2014 reports royalties due based on sales or use of the production. `}</p>
    <br />
    <p>{`Solid minerals companies are only required to submit one monthly `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Production and Royalty (P&R) Report, Form ONRR-4430 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <h4 {...{
      "id": "Validation",
      "style": {
        "position": "relative"
      }
    }}>{`Validation`}</h4>
    <p>{`ONRR verifies royalties through its compliance management program. This program ensures that the royalty revenues generated from the sales of commodities extracted from leased federal lands are accurately paid and reported. The compliance program includes validation of the reported data using upfront edits, data mining, audits, and compliance reviews.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Upfront edits.`}</strong>{` Data goes through initial upfront system edits before being accepted into ONRR’s financial systems.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Data mining.`}</strong>{` Data mining is a partially automated activity to identify and resolve data errors before audits and compliance reviews. It examines large sets of company-reported data for common errors, such as irregularities in the volume of oil or gas extracted. If errors are identified, ONRR staff work with companies to have them correct the data.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Audits.`}</strong>{` An audit involves detailed examinations of companies’ royalty payments and corresponding reporting. Audits ensure that royalty payments and other obligations to ONRR comply with lease terms, federal laws and regulations, and other policies.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Compliance reviews.`}</strong>{` A compliance review is an analysis that determines the reasonableness of company-reported production and royalty data.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "Datasets",
      "style": {
        "position": "relative"
      }
    }}>{`Datasets`}</h3>
    <p>{`ONRR maintains datasets and provides them to the public through this site. The datasets include production, revenue, and disbursements. These datasets each have unique attributes.`}</p>
    <h4 {...{
      "id": "Production-1",
      "style": {
        "position": "relative"
      }
    }}>{`Production`}</h4>
    <p>{`Production data found on this site comes from several places. Oil and gas companies report production on ONRR’s `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/production?tabs=forms"
      }}>{`Oil and Gas Operations Report (OGOR) `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. Companies report solid minerals production on ONRR’s `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Production and Royalty (P&R) Report, Form ONRR-4430 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. Companies use these forms to electronically report their production information. The reported data undergoes initial upfront system edits. After that,  it is accepted into ONRR’s financial systems.`}</p>
    <h4 {...{
      "id": "Revenue-2",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h4>
    <p>{`Revenue data found on this site comes from several places. Oil and gas companies report royalties, rents, and other revenues on `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/revenue?tabs=forms"
      }}>{`Form ONRR-2014 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. Companies report solid minerals royalties, rents, bonuses, and other revenues on `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Form ONRR-4430 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. Companies use these forms to electronically report their revenue payment information. This company-reported data goes through initial upfront system edits. After that, it is accepted into ONRR’s financial systems. Additional revenue comes from ONRR’s accounts receivable data. These payments are not tied to sales volumes and royalties. They are submitted to ONRR by companies and matched to ONRR-generated receivables. The accounts receivable revenues data includes payments for interest and civil penalties.`}</p>
    <h4 {...{
      "id": "Disbursements-1",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements`}</h4>
    <p>{`Disbursement data found on this site comes from ONRR. ONRR reconciles the company-reported royalty data and other revenue data with the payments to the U.S. Treasury. Then, ONRR disburses the funds from the Treasury account to the appropriate federal, state, or tribal accounts. All these transactions are reported and stored in ONRR’s systems.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      